import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navItems, site } from "@/mocks/site";
import { articles } from "@/mocks/articles";
import { formatHeat } from "@/utils/format";

const tickerItems = [...articles]
  .sort((a, b) => b.heat - a.heat)
  .slice(0, 6)
  .map((item) => item.title);

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setKeyword("");
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  const results = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return [];
    return articles
      .filter(
        (item) =>
          item.title.toLowerCase().includes(key) ||
          item.summary.toLowerCase().includes(key) ||
          item.tags.some((tag) => tag.toLowerCase().includes(key)),
      )
      .slice(0, 6);
  }, [keyword]);

  const goToArticle = (id: string) => {
    setSearchOpen(false);
    setKeyword("");
    navigate(`/article/${id}`);
  };

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="relative z-40">
      <div className="border-b border-primary-600/30 bg-primary-500 text-background-50">
        <div className="mx-auto flex h-9 max-w-[1400px] items-center gap-3 px-4 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold">
            <span className="gg-pulse-dot h-1.5 w-1.5 rounded-full bg-accent-400" />
            猛料速递
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="gg-marquee-track">
              {[...tickerItems, ...tickerItems].map((title, index) => (
                <Link
                  key={`${title}-${index}`}
                  to="/category/baoliao"
                  className="mr-10 whitespace-nowrap text-xs text-background-100 transition-colors hover:text-accent-400"
                >
                  {title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-4 px-4 md:h-[72px] md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5 cursor-pointer">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 md:h-10 md:w-10">
              <i className="ri-fire-fill text-lg md:text-xl" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-bold tracking-wide text-foreground-950 md:text-xl">
                {site.name}
              </span>
              <span className="hidden text-[11px] tracking-[0.2em] text-foreground-500 sm:block">
                {site.tagline}
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
            {navItems.slice(0, 6).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? "bg-primary-100 font-medium text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="搜索大瓜"
              onClick={() => setSearchOpen((prev) => !prev)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
            >
              <i className={`${searchOpen ? "ri-close-line" : "ri-search-line"} text-base`} />
            </button>
            <Link
              to="/contact"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:block cursor-pointer"
            >
              瓜友投稿
            </Link>
            <button
              type="button"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden cursor-pointer"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} />
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-background-200 bg-background-50">
            <div className="mx-auto max-w-[1400px] px-4 py-4 md:px-6">
              <div className="flex items-center gap-3 rounded-lg border border-background-300 bg-background-100 px-4 py-2.5">
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  ref={inputRef}
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && results[0]) goToArticle(results[0].id);
                    if (event.key === "Escape") setSearchOpen(false);
                  }}
                  type="text"
                  placeholder="搜索大瓜关键词，如：直播间、退费、停更"
                  className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                {keyword && (
                  <button
                    type="button"
                    aria-label="清空搜索"
                    onClick={() => setKeyword("")}
                    className="flex h-5 w-5 items-center justify-center text-foreground-400 hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-close-circle-line text-base" />
                  </button>
                )}
              </div>

              {keyword.trim() !== "" && (
                <div className="mt-3 divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200">
                  {results.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-foreground-500">
                      暂时没有找到相关瓜帖，换个关键词试试，或前往
                      <Link to="/contact" className="mx-1 text-primary-600 hover:underline">
                        投稿爆料
                      </Link>
                      。
                    </p>
                  ) : (
                    results.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => goToArticle(item.id)}
                        className="flex w-full items-center justify-between gap-4 bg-background-50 px-4 py-3 text-left transition-colors hover:bg-background-100 cursor-pointer"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground-900">
                            {item.title}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-foreground-500">
                            {item.summary}
                          </span>
                        </span>
                        <span className="shrink-0 text-xs text-primary-600">
                          {formatHeat(item.heat)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <nav aria-label="移动端导航" className="mx-auto max-w-[1400px] px-4 py-3 md:px-6">
              <ul className="grid grid-cols-2 gap-2">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`block whitespace-nowrap rounded-md px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                        isActive(item.path)
                          ? "bg-primary-100 font-medium text-primary-700"
                          : "bg-background-100 text-foreground-700 hover:text-primary-600"
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}